/**
 * Main game structure (must stay aligned with Backend/civic-discourse-backend/lib/constants.js).
 *
 * --- Previous layout (kept for reference) ---
 * 3 investment rounds → 5 min debate chat → 1 final investment round → end.
 * export const TOTAL_GAME_ROUNDS = 4;
 * export const ROUNDS_PER_CHAT = 3;
 * export const CHAT_DURATION_SECONDS = 5 * 60;
 *
 * --- Current layout ---
 * Practice round (solo, before partner match) → 10 min debate chat → 1 investment round → end.
 */
export const CHAT_FIRST = true;

/** Investment rounds in the main game (after the debate chat). */
export const TOTAL_GAME_ROUNDS = 1;

/** Debate chat anchor index (0 = chat happens before any investment round). */
export const ROUNDS_PER_CHAT = 0;

/** Chat length in seconds — must match backend `CHAT_DURATION_MS` */
export const CHAT_DURATION_SECONDS = 10 * 60;

/** Investment decision time in seconds */
export const ROUND_DURATION_SECONDS = 45;

/** Practice round (solo, before partner match) — must match backend `PRACTICE_ROUND_DURATION_MS` */
export const PRACTICE_ROUND_DURATION_SECONDS = 60;

/** Coins each player starts with at the beginning of every investment round. */
export const INITIAL_COINS_PER_ROUND = 30;

/** In-game discussion (Task 1) — keep in sync across Chat UI and post-game questions */
export const DEBATE_UMBRELLA_TOPICS = [
  'Is AI good for society?',
  'Does social media make society stronger by keeping people informed?',
  'Are mental health struggles actually more common today than in previous generations?',
] as const;

/** Kept in the list above; not assigned to new groups. */
const DISABLED_DEBATE_TOPICS: readonly string[] = [
  'Does social media make society stronger by keeping people informed?',
  'Are mental health struggles actually more common today than in previous generations?',
];

export const DEBATE_TOPIC_TITLE =
  DEBATE_UMBRELLA_TOPICS.find((topic) => !DISABLED_DEBATE_TOPICS.includes(topic)) ||
  DEBATE_UMBRELLA_TOPICS[0];
export const DEBATE_TOPIC_SUBTITLE = 'Artificial intelligence and whether it is good for society.';

export const DEBATE_LIKERT_BY_TOPIC: Record<string, string> = {
  'Is AI good for society?': 'Artificial intelligence is good for society.',
  'Does social media make society stronger by keeping people informed?':
    'Social media makes society stronger by keeping people informed.',
  'Are mental health struggles actually more common today than in previous generations?':
    'Mental health struggles are more common today than in previous generations.',
};

/** Static subtopics are empty at chat start; dynamic subtopics are generated during the debate. */
export const DEBATE_TOPIC_QUESTIONS: readonly string[] = [];

/** 1–7 Likert shown immediately before and after the in-game debate chat */
export const DEBATE_LIKERT_QUESTION =
  DEBATE_LIKERT_BY_TOPIC[DEBATE_TOPIC_TITLE] || 'Artificial intelligence is good for society.';

export function getDebateLikertQuestion(topic: string): string {
  const trimmed = (topic || '').trim();
  return DEBATE_LIKERT_BY_TOPIC[trimmed] || DEBATE_LIKERT_QUESTION;
}

export type DebatePhaseTiming = {
  roundStartTime?: number | null;
  chatStartTime?: number | null;
};

/**
 * True when the UI should show the debate chat (not the investment round).
 * Chat-first: round index 0 is debate until the server arms the investment clock
 * (phaseType 'round' with roundStartTime) after the debate window has actually elapsed.
 */
export function isDebateChatPhase(
  phaseType: string | null | undefined,
  currentRoundIndex: number,
  timing?: DebatePhaseTiming
): boolean {
  if (CHAT_FIRST && currentRoundIndex === 0) {
    if (phaseType !== 'round' || timing?.roundStartTime == null) {
      return true;
    }
    // Round clock armed — only leave debate if the debate timer actually ran.
    if (timing.chatStartTime == null) {
      return true;
    }
    const debateMs = timing.roundStartTime - timing.chatStartTime;
    const minDebateMs = CHAT_DURATION_SECONDS * 1000 - 3000;
    return debateMs < minDebateMs;
  }
  return phaseType === 'chat';
}

/** Build timing context from a checkPlayerStatus / nextRound payload. */
export function debatePhaseTimingFromPayload(payload: {
  roundStartTime?: number | null;
  chatStartTime?: number | null;
}): DebatePhaseTiming {
  return {
    roundStartTime: payload.roundStartTime ?? null,
    chatStartTime: payload.chatStartTime ?? null,
  };
}

/**
 * Seconds left for the header timer — never apply investment-round countdown while debate UI is active.
 */
export function resolveSyncedTimerSeconds(
  isDebateUi: boolean,
  payload: {
    phaseType?: string | null;
    timeRemaining?: number | null;
    chatStartTime?: number | null;
  }
): number | null {
  if (!isDebateUi) {
    if (payload.phaseType === 'round' && payload.timeRemaining != null) {
      return payload.timeRemaining;
    }
    return payload.timeRemaining ?? null;
  }

  if (payload.phaseType === 'chat' && payload.chatStartTime != null && payload.timeRemaining != null) {
    return payload.timeRemaining;
  }

  if (payload.chatStartTime != null) {
    const elapsed = (Date.now() - payload.chatStartTime) / 1000;
    return Math.max(0, Math.ceil(CHAT_DURATION_SECONDS - elapsed));
  }

  return null;
}
